import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Printer, ArrowLeft, Save, Loader2, Upload, ImageIcon, Plus, Trash2, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api, API_BASE } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/data/defaultContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Section = ({ title, desc, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6">
    <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
    {desc && <p className="mt-1 text-sm text-slate-500">{desc}</p>}
    <div className="mt-5 space-y-4">{children}</div>
  </div>
);

const ImagePicker = ({ imageId, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.id);
      toast.success("Image updated");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const genImage = async () => {
    if (!genPrompt.trim()) { toast.error("Describe the image first"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/ai/generate-image", { prompt: genPrompt });
      onUploaded(data.id);
      setGenOpen(false);
      setGenPrompt("");
      toast.success("AI image set");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenBusy(false);
    }
  };
  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="h-16 w-24 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 grid place-items-center shrink-0">
          {imageId ? (
            <img src={`${API_BASE}/content/image/${imageId}`} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-slate-300" />
          )}
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {imageId ? "Replace" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
        <button type="button" onClick={() => setGenOpen((o) => !o)} className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors">
          <Sparkles size={15} /> Generate AI
        </button>
      </div>
      {genOpen && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <Input value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} placeholder="Describe the image, e.g. glowing LED name board at night" className="flex-1" />
          <Button type="button" size="sm" className="gap-2 bg-primary hover:bg-amber-700 text-white" onClick={genImage} disabled={genBusy}>
            {genBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
          </Button>
        </div>
      )}
    </div>
  );
};

const BannersManager = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [linkDrafts, setLinkDrafts] = useState({});
  const load = async () => {
    try {
      const { data } = await api.get("/banners");
      setItems(data);
      setDrafts(Object.fromEntries(data.map((b) => [b.id, b.caption || ""])));
      setLinkDrafts(Object.fromEntries(data.map((b) => [b.id, b.link || "quote"])));
    } catch { /* noop */ }
  };
  const saveCaption = async (id) => {
    try { await api.patch(`/banners/${id}`, { caption: drafts[id] || "", link: linkDrafts[id] || "quote" }); toast.success("Banner saved"); }
    catch { toast.error("Save failed"); }
  };
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try { await api.post("/banners", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch { toast.error(`Upload failed: ${file.name}`); }
    }
    setBusy(false);
    e.target.value = "";
    toast.success("Banner added");
    load();
  };
  const remove = async (id) => {
    try { await api.delete(`/banners/${id}`); setItems((p) => p.filter((b) => b.id !== id)); toast.success("Banner removed"); }
    catch { toast.error("Delete failed"); }
  };
  return (
    <Section title="Home Page Banners (3D)" desc="Wide photos that rotate in the 3D banner on your homepage. Landscape images look best.">
      <label data-testid="banner-upload-label" className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Add banner photos
        <input data-testid="banner-file-input" type="file" accept="image/*" multiple className="hidden" onChange={upload} />
      </label>
      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((b) => (
            <div key={b.id} data-testid={`banner-item-${b.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-slate-200 p-3">
              <img src={`${API_BASE}/banners/${b.id}/image`} alt="banner" className="h-16 w-28 shrink-0 rounded-md object-cover" />
              <Input
                data-testid={`banner-caption-${b.id}`}
                value={drafts[b.id] ?? ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [b.id]: e.target.value }))}
                placeholder="Slide headline (e.g. Same-day flex banners)"
                className="flex-1"
              />
              <select
                data-testid={`banner-link-${b.id}`}
                value={linkDrafts[b.id] || "quote"}
                onChange={(e) => setLinkDrafts((d) => ({ ...d, [b.id]: e.target.value }))}
                className="rounded-md border border-input bg-white px-3 py-2 text-sm"
              >
                <option value="quote">Button → Get Quote</option>
                <option value="services">Button → View Services</option>
                <option value="pricing">Button → See Pricing</option>
                <option value="area">Button → Service Area</option>
              </select>
              <Button type="button" size="sm" variant="outline" data-testid={`banner-caption-save-${b.id}`} onClick={() => saveCaption(b.id)}>Save</Button>
              <button data-testid={`banner-delete-${b.id}`} onClick={() => remove(b.id)} className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-red-500 hover:bg-red-50">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

export default function AdminContent() {
  const { user } = useAuth();
  const { refresh } = useContent();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setC({ ...DEFAULT_CONTENT, ...data, hero: { ...DEFAULT_CONTENT.hero, ...(data.hero || {}) }, contact: { ...DEFAULT_CONTENT.contact, ...(data.contact || {}) } });
    } catch {
      setC(DEFAULT_CONTENT);
    }
  }, []);

  useEffect(() => {
    if (user && user.email) load();
  }, [user, load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", c);
      await refresh();
      toast.success("Website updated — changes are live");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setHero = (k, v) => setC((p) => ({ ...p, hero: { ...p.hero, [k]: v } }));
  const setContact = (k, v) => setC((p) => ({ ...p, contact: { ...p.contact, [k]: v } }));
  const setService = (i, k, v) => setC((p) => ({ ...p, services: p.services.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const setRow = (list, i, k, v) => setC((p) => ({ ...p, [list]: p[list].map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const addRow = (list, blank) => setC((p) => ({ ...p, [list]: [...p[list], blank] }));
  const delRow = (list, i) => setC((p) => ({ ...p, [list]: p[list].filter((_, idx) => idx !== i) }));

  if (!user || !user.email || !c) {
    return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-white"><Printer size={20} /></span>
            <h1 className="font-heading text-base font-extrabold text-slate-900">Edit Website</h1>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" data-testid="view-live-site-btn">
              <Button variant="outline" size="sm" className="gap-2"><ExternalLink size={15} /> <span className="hidden sm:inline">View live site</span></Button>
            </a>
            <Link to="/admin/dashboard" data-testid="content-back-btn"><Button variant="outline" size="sm" className="gap-2"><ArrowLeft size={15} /> Dashboard</Button></Link>
            <Button data-testid="content-save-btn" size="sm" className="gap-2 bg-primary hover:bg-amber-700 text-white" onClick={save} disabled={saving}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-6">
        <p className="text-sm text-slate-500">Editing English content. Kannada text stays as-is. Changes go live after Save.</p>

        <BannersManager />

        <Section title="Hero Section" desc="The top banner of your website.">
          <ImagePicker imageId={c.hero.image_id} onUploaded={(id) => setHero("image_id", id)} />
          <div><Label>Badge</Label><Input data-testid="hero-badge" className="mt-1.5" value={c.hero.badge} onChange={(e) => setHero("badge", e.target.value)} /></div>
          <div><Label>Headline</Label><Textarea data-testid="hero-title" className="mt-1.5" rows={2} value={c.hero.title} onChange={(e) => setHero("title", e.target.value)} /></div>
          <div><Label>Subtext</Label><Textarea data-testid="hero-sub" className="mt-1.5" rows={3} value={c.hero.sub} onChange={(e) => setHero("sub", e.target.value)} /></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Badge stat 1</Label><Input className="mt-1.5" value={c.hero.stat1} onChange={(e) => setHero("stat1", e.target.value)} /></div>
            <div><Label>Stat 2</Label><Input className="mt-1.5" value={c.hero.stat2} onChange={(e) => setHero("stat2", e.target.value)} /></div>
            <div><Label>Stat 3</Label><Input className="mt-1.5" value={c.hero.stat3} onChange={(e) => setHero("stat3", e.target.value)} /></div>
          </div>
        </Section>

        <Section title="Service Cards" desc="Name, description, price and photo for each service.">
          {c.services.map((s, i) => (
            <div key={s.id || i} data-testid={`content-service-${s.id}`} className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-3">
              <ImagePicker imageId={s.image_id} onUploaded={(id) => setService(i, "image_id", id)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input className="mt-1.5" value={s.name} onChange={(e) => setService(i, "name", e.target.value)} /></div>
                <div><Label>Price label</Label><Input className="mt-1.5" value={s.price} onChange={(e) => setService(i, "price", e.target.value)} /></div>
              </div>
              <div><Label>Description</Label><Textarea className="mt-1.5" rows={2} value={s.desc} onChange={(e) => setService(i, "desc", e.target.value)} /></div>
            </div>
          ))}
        </Section>

        <Section title="Fixed Price Products" desc="Ready-rate items shown in the pricing table.">
          {c.fixed_products.map((p, i) => (
            <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input className="mt-1.5" value={p.name} onChange={(e) => setRow("fixed_products", i, "name", e.target.value)} /></div>
                <div><Label>Price</Label><Input className="mt-1.5" value={p.price} onChange={(e) => setRow("fixed_products", i, "price", e.target.value)} /></div>
                <div><Label>Unit / note</Label><Input className="mt-1.5" value={p.unit} onChange={(e) => setRow("fixed_products", i, "unit", e.target.value)} /></div>
                <div><Label>Turnaround</Label><Input className="mt-1.5" value={p.turn} onChange={(e) => setRow("fixed_products", i, "turn", e.target.value)} /></div>
              </div>
              <button className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline" onClick={() => delRow("fixed_products", i)}><Trash2 size={13} /> Remove</button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-2" onClick={() => addRow("fixed_products", { name: "", price: "", unit: "", turn: "" })}><Plus size={15} /> Add product</Button>
        </Section>

        <Section title='Custom "Starting From" Jobs' desc="Quote-only items with a starting price.">
          {c.custom_products.map((p, i) => (
            <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input className="mt-1.5" value={p.name} onChange={(e) => setRow("custom_products", i, "name", e.target.value)} /></div>
                <div><Label>Starting price</Label><Input className="mt-1.5" value={p.price} onChange={(e) => setRow("custom_products", i, "price", e.target.value)} /></div>
              </div>
              <div className="mt-3"><Label>Description</Label><Textarea className="mt-1.5" rows={2} value={p.desc} onChange={(e) => setRow("custom_products", i, "desc", e.target.value)} /></div>
              <button className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline" onClick={() => delRow("custom_products", i)}><Trash2 size={13} /> Remove</button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-2" onClick={() => addRow("custom_products", { name: "", price: "", desc: "" })}><Plus size={15} /> Add job</Button>
        </Section>

        <Section title="Contact Details" desc="Used across the whole site — buttons, footer, map.">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Phone (dial)</Label><Input data-testid="contact-phone" className="mt-1.5" value={c.contact.phone} onChange={(e) => setContact("phone", e.target.value)} /></div>
            <div><Label>Phone (display)</Label><Input className="mt-1.5" value={c.contact.phoneDisplay} onChange={(e) => setContact("phoneDisplay", e.target.value)} /></div>
            <div><Label>WhatsApp (digits, no +)</Label><Input data-testid="contact-whatsapp" className="mt-1.5" value={c.contact.whatsapp} onChange={(e) => setContact("whatsapp", e.target.value)} /></div>
            <div><Label>Email</Label><Input className="mt-1.5" value={c.contact.email} onChange={(e) => setContact("email", e.target.value)} /></div>
          </div>
          <div><Label>Address</Label><Textarea className="mt-1.5" rows={2} value={c.contact.address} onChange={(e) => setContact("address", e.target.value)} /></div>
          <div><Label>Business hours</Label><Input className="mt-1.5" value={c.contact.hours} onChange={(e) => setContact("hours", e.target.value)} /></div>
        </Section>

        <div className="flex justify-end pb-10">
          <Button data-testid="content-save-btn-bottom" className="gap-2 bg-primary hover:bg-amber-700 text-white" onClick={save} disabled={saving}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
          </Button>
        </div>
      </main>
    </div>
  );
}
